import React, { useState } from 'react'
import { X, AlertTriangle, Trash2 } from 'lucide-react'
import toast from 'react-hot-toast'
import hrShiftService from '../../services/hrShiftService'

const DeleteShiftModal = ({ isOpen, onClose, shift, onShiftDeleted }) => {
  const [isDeleting, setIsDeleting] = useState(false)
  const [confirmText, setConfirmText] = useState('')

  if (!isOpen || !shift) return null

  const handleDelete = async () => {
    setIsDeleting(true)
    
    try {
      await hrShiftService.deleteShift(shift.id)
      toast.success('Shift deleted successfully!')
      onShiftDeleted()
      onClose()
    } catch (error) {
      console.error('Delete shift error:', error)
      const errorMessage = error.response?.data?.message || 'Failed to delete shift'
      toast.error(errorMessage)
    } finally {
      setIsDeleting(false)
    }
  }

  const handleCancel = () => {
    if (!isDeleting) {
      setConfirmText('')
      onClose()
    }
  }

  // Helper function to safely get property value
  const safeGet = (obj, path, defaultValue = 'N/A') => {
    try {
      return path.split('.').reduce((current, key) => {
        return current && current[key] !== undefined ? current[key] : defaultValue
      }, obj)
    } catch {
      return defaultValue
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
              <Trash2 className="h-4 w-4 text-red-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900">Delete Shift</h2>
          </div>
          <button
            onClick={handleCancel}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={isDeleting}
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Warning Icon and Message */}
          <div className="flex items-center justify-center mb-6">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
              <AlertTriangle className="h-8 w-8 text-red-600" />
            </div>
          </div>

          <div className="text-center mb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Are you sure you want to delete this shift?
            </h3>
            <p className="text-sm text-gray-500">
              This action cannot be undone. This will permanently delete the shift and all associated data.
            </p>
          </div>

          {/* Shift Information */}
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm font-medium text-gray-700">Shift Name:</span>
                <span className="text-sm text-gray-900">{safeGet(shift, 'shiftName')}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm font-medium text-gray-700">Shift Code:</span>
                <span className="text-sm text-gray-900">{safeGet(shift, 'shiftCode')}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm font-medium text-gray-700">Status:</span>
                <span className={`text-sm font-medium ${
                  safeGet(shift, 'status') === 'active' ? 'text-green-600' : 'text-red-600'
                }`}>
                  {safeGet(shift, 'status', 'Unknown').charAt(0).toUpperCase() + 
                   safeGet(shift, 'status', 'Unknown').slice(1)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm font-medium text-gray-700">Working Hours:</span>
                <span className="text-sm text-gray-900">{safeGet(shift, 'workingHours', 0)} Hours</span>
              </div>
            </div>
          </div>

          {/* Warning Text */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <div className="flex items-start space-x-3">
              <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5" />
              <div>
                <h4 className="text-sm font-medium text-yellow-800 mb-1">
                  Warning: Irreversible Action
                </h4>
                <ul className="text-sm text-yellow-700 space-y-1">
                  <li>• All shift data will be permanently deleted</li>
                  <li>• Employee schedules using this shift may be affected</li>
                  <li>• Historical records may become incomplete</li>
                  <li>• This action cannot be undone</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Confirmation Input */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Type "<span className="font-bold text-red-600">DELETE</span>" to confirm:
            </label>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              placeholder="Type DELETE to confirm"
              disabled={isDeleting}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 px-6 py-4 border-t bg-gray-50">
          <button
            onClick={handleCancel}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-50"
            disabled={isDeleting}
          >
            Cancel
          </button>
          <button
            onClick={handleDelete}
            disabled={isDeleting || confirmText !== 'DELETE'}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            {isDeleting ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span>Deleting...</span>
              </>
            ) : (
              <>
                <Trash2 className="h-4 w-4" />
                <span>Delete Shift</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

export default DeleteShiftModal
